import { useEffect, useState } from "react";
import { Clock, FileDown, KeyRound, QrCode, RefreshCw, Pencil } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { JORNADA_TAB } from "@/constants/testIds";
import { toast } from "sonner";

const STATUS_LABELS = {
  sin_marcar: { label: "Sin marcar", color: "text-charcoal/50" },
  en_turno: { label: "En turno", color: "text-emerald-700" },
  completo: { label: "Completo", color: "text-petroleum" },
};

export function JornadaTab({ employeeId }) {
  const [summary, setSummary] = useState([]);
  const [correctOpen, setCorrectOpen] = useState(null); // { sequence, event_type, date }
  const [correctForm, setCorrectForm] = useState({ timestamp: "", reason: "" });
  const [qrToken, setQrToken] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  const load = () => api.get(`/employees/${employeeId}/timeclock`).then(({ data }) => setSummary(data.daily_summary));

  useEffect(() => { load(); }, [employeeId]);

  const resetPin = async () => {
    try {
      const { data } = await api.post(`/employees/${employeeId}/timeclock/pin/reset`);
      toast.success(
        <span data-testid={JORNADA_TAB.pinRevealValue}>Nuevo PIN de kiosko: <strong>{data.pin}</strong> — anótalo ahora, no se puede volver a ver</span>,
        { duration: 15000 }
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo generar el PIN");
    }
  };

  const showQr = async () => {
    const { data } = await api.get(`/employees/${employeeId}/timeclock/qr`);
    setQrToken(data.qr_token);
    setQrOpen(true);
  };

  const regenerateQr = async () => {
    const { data } = await api.post(`/employees/${employeeId}/timeclock/qr/regenerate`);
    setQrToken(data.qr_token);
    toast.success("Código QR regenerado — el anterior ya no funciona");
  };

  const submitCorrection = async () => {
    try {
      await api.post(`/employees/${employeeId}/timeclock/correct`, {
        corrects_sequence: correctOpen.sequence,
        event_type: correctOpen.event_type,
        timestamp: correctForm.timestamp,
        reason: correctForm.reason,
      });
      toast.success("Corrección registrada y empleado notificado en Avisos");
      setCorrectOpen(null);
      setCorrectForm({ timestamp: "", reason: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo registrar la corrección");
    }
  };

  const download = async () => {
    const res = await api.get(`/employees/${employeeId}/timeclock/export?format=pdf`, { responseType: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(res.data);
    link.download = "jornada.pdf";
    link.click();
  };

  return (
    <div data-testid={JORNADA_TAB.container} className="mt-4 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid={JORNADA_TAB.exportButton} size="sm" variant="outline" onClick={download}><FileDown className="w-4 h-4 mr-1.5" /> Exportar PDF</Button>
        <Button data-testid={JORNADA_TAB.pinResetButton} size="sm" variant="outline" onClick={resetPin}><KeyRound className="w-4 h-4 mr-1.5" /> Generar PIN de kiosko</Button>
        <Button data-testid={JORNADA_TAB.qrShowButton} size="sm" variant="outline" onClick={showQr}><QrCode className="w-4 h-4 mr-1.5" /> Ver código QR</Button>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-bone text-center">
          <DialogHeader><DialogTitle>Código QR del kiosko</DialogTitle></DialogHeader>
          {qrToken && <div className="flex justify-center p-4"><QRCodeSVG value={qrToken} size={200} /></div>}
          <p className="text-xs text-charcoal/50">Imprime este código en su gafete. Quien lo tenga puede marcar por esta persona, igual que una credencial física.</p>
          <Button data-testid={JORNADA_TAB.qrRegenerateButton} variant="outline" onClick={regenerateQr}><RefreshCw className="w-4 h-4 mr-1.5" /> Regenerar (invalida el anterior)</Button>
        </DialogContent>
      </Dialog>

      <div className="bg-card border border-petroleum/10 rounded-xl divide-y divide-petroleum/10">
        {summary.slice().reverse().map((d) => {
          const s = STATUS_LABELS[d.status] || STATUS_LABELS.sin_marcar;
          return (
            <div key={d.date} data-testid={JORNADA_TAB.dayRow} className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-charcoal flex items-center gap-2"><Clock className="w-4 h-4 text-petroleum" strokeWidth={1.5} /> {d.date}</p>
                <span className={`text-xs font-semibold ${s.color}`}>{s.label}{d.worked_hours != null && ` · ${d.worked_hours} h`}</span>
              </div>
              <div className="flex flex-wrap gap-4 mt-2 text-xs text-charcoal/70">
                <span>Entrada: {d.entrada_time ? `${d.entrada_time} (${d.entrada_method === "admin_capture" ? "capturado por admin" : d.entrada_method})` : "sin marcar"}</span>
                {d.entrada_sequence && (
                  <Button data-testid={JORNADA_TAB.correctButton} size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setCorrectOpen({ sequence: d.entrada_sequence, event_type: "entrada", date: d.date })}>
                    <Pencil className="w-3 h-3 mr-1" /> Corregir
                  </Button>
                )}
                <span>Salida: {d.salida_time ? `${d.salida_time} (${d.salida_method === "admin_capture" ? "capturado por admin" : d.salida_method})` : "sin marcar"}</span>
                {d.salida_sequence && (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setCorrectOpen({ sequence: d.salida_sequence, event_type: "salida", date: d.date })}>
                    <Pencil className="w-3 h-3 mr-1" /> Corregir
                  </Button>
                )}
              </div>
              {[...(Array.isArray(d.entrada_corrections) ? d.entrada_corrections : []), ...(Array.isArray(d.salida_corrections) ? d.salida_corrections : [])].map((c, i) => (
                <p key={i} className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5 mt-2">
                  Corregido el {c.timestamp} — {c.reason} (por {c.captured_by})
                </p>
              ))}
            </div>
          );
        })}
        {summary.length === 0 && <p className="p-6 text-sm text-charcoal/50">Sin marcajes de jornada todavía.</p>}
      </div>

      <Dialog open={!!correctOpen} onOpenChange={(o) => !o && setCorrectOpen(null)}>
        <DialogContent data-testid={JORNADA_TAB.correctDialog} className="bg-bone">
          <DialogHeader><DialogTitle>Corregir {correctOpen?.event_type} del {correctOpen?.date}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Hora correcta</Label>
              <Input type="datetime-local" value={correctForm.timestamp} onChange={(e) => setCorrectForm({ ...correctForm, timestamp: e.target.value })} className="bg-white mt-1" />
            </div>
            <div>
              <Label>Motivo (obligatorio)</Label>
              <Input data-testid={JORNADA_TAB.correctReason} value={correctForm.reason} onChange={(e) => setCorrectForm({ ...correctForm, reason: e.target.value })} placeholder="Ej. Se le olvidó marcar, confirmado con su supervisor" className="bg-white mt-1" />
            </div>
            <p className="text-xs text-charcoal/50">El registro original nunca se borra — esta corrección queda anexada y visible, con tu nombre y motivo.</p>
            <Button data-testid={JORNADA_TAB.correctSubmit} onClick={submitCorrection} disabled={!correctForm.timestamp || !correctForm.reason} className="w-full bg-petroleum rounded-full">Registrar corrección</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
